import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import dashboardAPI from '../../services/dashboard';
import PaginationComponent from '../PaginationComponent';
import VoucherDetailModal from '../cashier/VoucherDetailModal';
import {
  PAYMENT_METHOD_LABELS,
  VOUCHER_STATUS_COLORS,
  VOUCHER_STATUS_LABELS,
} from '../../utils/cashierConstants';

/**
 * #235631 — sếp: "ở phần Dashboard có hiển thị đã chi bao nhiêu, khi bấm vào sẽ show
 * một giao diện nhỏ có hiển thị rõ tất cả thông tin phiếu chi cấu thành nên con số
 * đã chi trong nguyên vật liệu đó".
 *
 * Dữ liệu lấy từ `/dashboard/expense-vouchers` — endpoint dùng CHUNG bộ lọc với
 * `/dashboard/revenue-report`, nên `total_amount` khớp tuyệt đối con số ngoài
 * Dashboard. `expectedTotal` truyền vào chỉ để ĐỐI CHIẾU: lệch là hiện cảnh báo đỏ
 * chứ không im lặng.
 */

const LIMIT = 20;

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
};

const AMOUNT_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const ExpenseVouchersModal = ({
  show,
  onHide,
  title = 'Chi phí nguyên vật liệu (NVL)',
  group = 'MATERIAL',
  branchId,
  branchName,
  fromDate,
  toDate,
  expectedTotal,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [detailVoucherId, setDetailVoucherId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getExpenseVouchers({
        group,
        page,
        limit: LIMIT,
        ...(branchId ? { branch_id: branchId } : {}),
        ...(fromDate ? { from_date: fromDate } : {}),
        ...(toDate ? { to_date: toDate } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Không tải được danh sách phiếu chi');
    } finally {
      setLoading(false);
    }
  }, [group, page, branchId, fromDate, toDate]);

  useEffect(() => {
    if (!show) return;
    fetchData();
  }, [show, fetchData]);

  // Mở lại modal thì luôn về trang 1 để không thấy trang cũ của lần xem trước.
  useEffect(() => { if (show) setPage(1); }, [show, branchId, fromDate, toDate, group]);

  const rows = result?.data || [];
  const total = result?.meta?.total ?? 0;
  const totalAmount = result?.total_amount ?? 0;
  const mismatch =
    expectedTotal != null && result != null && Math.round(Number(expectedTotal)) !== Math.round(Number(totalAmount));

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <div>
            <Modal.Title className="h5 mb-0">{title}</Modal.Title>
            <div className="small text-muted">
              {branchName || 'Tất cả chi nhánh'}
              {fromDate && toDate && <> · {formatDate(fromDate)} – {formatDate(toDate)}</>}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Đang tải danh sách phiếu chi...
            </div>
          ) : error ? (
            <Alert variant="danger" className="mb-0">
              {error}
              <div className="mt-2">
                <Button size="sm" variant="outline-danger" onClick={fetchData}>Thử lại</Button>
              </div>
            </Alert>
          ) : (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3 p-3 bg-light rounded">
                <div>
                  <div className="text-muted small">Tổng cộng {total} phiếu chi đã duyệt</div>
                  <div className="h4 mb-0" style={AMOUNT_STYLE}>{formatCurrency(totalAmount)}đ</div>
                </div>
                {mismatch && (
                  <Alert variant="danger" className="mb-0 py-2 px-3 small">
                    ⚠️ Lệch với con số ngoài Dashboard ({formatCurrency(expectedTotal)}đ) —
                    chênh {formatCurrency(Number(expectedTotal) - Number(totalAmount))}đ.
                  </Alert>
                )}
              </div>

              {total === 0 ? (
                <div className="text-center text-muted py-5">
                  Không có phiếu chi nào trong khoảng thời gian và chi nhánh đang chọn.
                </div>
              ) : (
                <>
                  {/* Máy tính: bảng */}
                  <div className="d-none d-md-block table-responsive">
                    <Table hover size="sm" className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Mã phiếu</th>
                          <th>Ngày chi</th>
                          <th>Chi nhánh</th>
                          <th>Nội dung chi</th>
                          <th className="text-end">Số tiền</th>
                          <th>Trạng thái</th>
                          <th>Đề nghị / duyệt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((v) => (
                          <tr
                            key={v.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setDetailVoucherId(v.id)}
                            title="Bấm để xem phiếu chi đầy đủ"
                          >
                            <td className="fw-semibold text-primary text-nowrap">{v.voucher_number}</td>
                            <td className="text-nowrap">{formatDate(v.voucher_date)}</td>
                            <td className="small">{v.branch_name}</td>
                            <td>
                              <div>{v.description}</div>
                              <div className="small text-muted">
                                {v.category_label}
                                {' · '}
                                {PAYMENT_METHOD_LABELS[v.payment_method] || v.payment_method}
                                {v.recipient_name && v.recipient_name !== 'N/A' && <> · Người nhận: {v.recipient_name}</>}
                              </div>
                            </td>
                            <td className="text-end fw-bold" style={AMOUNT_STYLE}>{formatCurrency(v.amount)}đ</td>
                            <td>
                              <Badge bg={VOUCHER_STATUS_COLORS[v.status] || 'secondary'}>
                                {VOUCHER_STATUS_LABELS[v.status] || v.status}
                              </Badge>
                            </td>
                            <td className="small">
                              <div>{v.created_by_name || '-'}</div>
                              <div className="text-muted">{v.approved_by_name || '-'}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Điện thoại: thẻ */}
                  <div className="d-md-none">
                    {rows.map((v) => (
                      <div
                        key={v.id}
                        className="border-bottom py-2"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setDetailVoucherId(v.id)}
                      >
                        <div className="d-flex justify-content-between gap-2">
                          <span className="fw-semibold text-primary">{v.voucher_number}</span>
                          <span className="fw-bold" style={AMOUNT_STYLE}>{formatCurrency(v.amount)}đ</span>
                        </div>
                        <div>{v.description}</div>
                        <div className="small text-muted">
                          {formatDate(v.voucher_date)} · {v.branch_name} · {v.category_label}
                        </div>
                        <div className="small text-muted">
                          Đề nghị: {v.created_by_name || '-'} · Duyệt: {v.approved_by_name || '-'}
                        </div>
                      </div>
                    ))}
                  </div>

                  {total > LIMIT && (
                    <div className="mt-3">
                      <PaginationComponent
                        totalItems={total}
                        itemsPerPage={LIMIT}
                        currentPage={page}
                        onPageChange={setPage}
                        loading={loading}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Đóng</Button>
        </Modal.Footer>
      </Modal>

      <VoucherDetailModal
        voucherId={detailVoucherId}
        show={!!detailVoucherId}
        onHide={() => setDetailVoucherId(null)}
      />
    </>
  );
};

export default ExpenseVouchersModal;
